// May this person see the developer tools?
//
// Two ways in, and they answer different questions. `DEV_TOOLS` is a *build*
// fact — `npm start` and the e2e bundle — decided at compile time and therefore
// tree-shaken out of a production bundle entirely. This is the other half: on a
// deployed build the panel is offered to whoever's **account** may have it, so
// the tools stop being a thing you can only reach by running the repo.
//
// **The UI gate is the whole gate here, and that is only honest because of
// where the commands go.** Everything the panel dispatches — `dev-grow`,
// `dev-defeat`, `dev-material`, `grant-card`, `set-power`, `pave` — is in
// `SOLO_COMMANDS`, so `server/rooms.ts` refuses every one of them on a
// networked room whoever is asking and whatever this answers. What is left is a
// single-player game, which is local: there is no server in it to be
// authoritative, and no other player's board to reach. So this decides what to
// *draw*, exactly as `UsersPage`'s ability does, and nothing about it is load
// bearing for anybody else's game. Do not widen it into a reason to let a dev
// command cross the socket — that refusal is what makes this safe.

import { useEffect, useState } from "react";
import { createMongoAbility } from "@casl/ability";

/**
 * The build-time gate, the same expression `debugHook.ts`'s `DEV_TOOLS` is:
 * `DEV` is `npm start`, `VITE_E2E` the built server the net smoke drives. Said
 * here rather than imported, so the client reads no engine module for it.
 */
const DEV_TOOLS: boolean = Boolean(import.meta.env.DEV || import.meta.env.VITE_E2E);

/**
 * Read the answer off a `/api/session` body.
 *
 * Split out and **total**, the shape every reader of that endpoint takes here:
 * a server that predates the `Dev` subject, a body that failed to parse, a dev
 * server answering something else and a signed-out visitor all come back
 * `false`. That is the direction to fail in — a client that guessed *open*
 * would draw a panel on a build that has no tools in it.
 *
 * Pure, so the rule it encodes is tested without a browser or a database.
 */
export function devAllowedBy(body: unknown): boolean {
  const session = body as { rules?: unknown; approved?: unknown } | null;
  // **Approval as well as the role, because `/api/session` sends the rules for
  // anybody signed in.** `staff()` on the server refuses an unapproved account
  // before it ever asks the ability, so reading the rules alone would draw the
  // panel for somebody the server treats as a stranger. Asked here rather than
  // fixed at the endpoint: narrowing what that body carries would change what
  // `UsersPage` draws too, which is a decision about the admin screen and not
  // about this.
  if (session?.approved !== true) return false;
  const rules = session.rules;
  if (!Array.isArray(rules)) return false;
  // The roles table is the engine's (`rbac/ability.ts`); only the one
  // question is asked here, in CASL's own words.
  return createMongoAbility<[string, string]>(rules as never).can("read", "Dev");
}

/**
 * One fetch for the whole page.
 *
 * Both callers self-gate — `DevMenu` and `PlanningDrawer` each return null on
 * their own rather than being prop-drilled through `GameShell` — so without
 * this they would ask twice on every mount. The promise is cached, never the
 * answer, so a failure is not remembered as a permanent no.
 */
let asking: Promise<boolean> | null = null;

function askOnce(): Promise<boolean> {
  asking ??= fetch("/api/session", { credentials: "same-origin" })
    .then((response) => response.json())
    .then(devAllowedBy)
    .catch(() => {
      // No answer is not a no *for ever* — a dev server with no `/api/session`
      // at all is the common case here, and so is a redeploy mid-flight.
      asking = null;
      return false;
    });
  return asking;
}

/**
 * Whether to draw the developer tools.
 *
 * `DEV_TOOLS` short-circuits, so a local `npm start` never touches the network
 * for this and the smokes are unaffected — which is what keeps the e2e suite
 * from depending on a session endpoint it has no account for.
 */
export function useDevTools(): boolean {
  const [allowed, setAllowed] = useState(DEV_TOOLS);
  useEffect(() => {
    if (DEV_TOOLS) return;
    let live = true;
    void askOnce().then((answer) => live && setAllowed(answer));
    return () => {
      live = false;
    };
  }, []);
  return allowed;
}
